import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, Zap, Shield, Brain, MessageSquare, FileText, BarChart3, Menu, X, Users, Clock, TrendingUp, Lock, Mail, Phone, MapPin } from 'lucide-react';

export default function MaverantHomepage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your interest! We will contact you shortly.');
    setFormData({ name: '', email: '', company: '', phone: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-8">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xl">M</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">Maverant</span>
                </div>
              </div>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#solutions" className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer">Solutions</a>
              <a href="#why-us" className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer">Why Us</a>
              <a href="#contact" className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer">Contact</a>
              <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg hover:from-teal-600 hover:to-cyan-700 transition-all font-semibold shadow-md cursor-pointer">
                Request Demo
              </button>
            </div>

            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-4">
              <button onClick={() => { setMobileMenuOpen(false); document.getElementById('solutions').scrollIntoView({ behavior: 'smooth' }); }} className="block w-full text-left text-gray-700 hover:text-teal-600 font-medium cursor-pointer">Solutions</button>
              <button onClick={() => { setMobileMenuOpen(false); document.getElementById('why-us').scrollIntoView({ behavior: 'smooth' }); }} className="block w-full text-left text-gray-700 hover:text-teal-600 font-medium cursor-pointer">Why Us</button>
              <button onClick={() => { setMobileMenuOpen(false); document.getElementById('contact').scrollIntoView({ behavior: 'smooth' }); }} className="block w-full text-left text-gray-700 hover:text-teal-600 font-medium cursor-pointer">Contact</button>
              <button onClick={() => { setMobileMenuOpen(false); document.getElementById('contact').scrollIntoView({ behavior: 'smooth' }); }} className="w-full px-6 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg hover:from-teal-600 hover:to-cyan-700 font-semibold cursor-pointer">
                Request Demo
              </button>
            </div>
          )}
        </div>
      </nav>

      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-teal-50 via-white to-cyan-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Brain className="w-4 h-4" />
              <span>AI-Powered Healthcare Innovation</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              Transform Healthcare
              <span className="block bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">Operations at Scale</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-10 leading-relaxed">
              Enterprise-grade AI solutions that streamline utilization management and correspondence operations for healthcare payers and providers.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg hover:from-teal-600 hover:to-cyan-700 transition-all transform hover:scale-105 flex items-center justify-center space-x-2 shadow-xl font-semibold text-lg">
                <span>Schedule Demo</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button onClick={() => document.getElementById('solutions').scrollIntoView({ behavior: 'smooth' })} className="px-8 py-4 bg-white text-gray-900 rounded-lg hover:bg-gray-50 transition-all border-2 border-gray-300 font-semibold text-lg shadow-md">
                View Solutions
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="text-3xl font-bold text-teal-600 mb-2">$3.5 to $1</div>
                <div className="text-sm text-gray-600">Cost per authorization</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="text-3xl font-bold text-teal-600 mb-2">94%</div>
                <div className="text-sm text-gray-600">Physician approval rate</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="text-3xl font-bold text-teal-600 mb-2">6 Mo</div>
                <div className="text-sm text-gray-600">Implementation time</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="text-3xl font-bold text-teal-600 mb-2">99.9%</div>
                <div className="text-sm text-gray-600">System uptime</div>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap justify-center items-center gap-8 text-gray-500">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5" />
                <span className="text-sm font-medium">HIPAA Compliant</span>
              </div>
              <div className="flex items-center space-x-2">
                <Lock className="w-5 h-5" />
                <span className="text-sm font-medium">API enabled</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Enterprise Ready</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              The Healthcare Operations Crisis
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Outdated systems are failing healthcare organizations, leading to delays, increased costs, and poor patient outcomes
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-md border-l-4 border-red-500">
              <div className="text-5xl font-bold text-red-600 mb-3">85%</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Manual Process Burden</h3>
              <p className="text-gray-600">Health plans cite high operational costs due to fragmented systems and manual workflows</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md border-l-4 border-orange-500">
              <div className="text-5xl font-bold text-orange-600 mb-3">94%</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Provider Delays</h3>
              <p className="text-gray-600">Of physicians report delays in care due to prior authorization bottlenecks</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md border-l-4 border-yellow-500">
              <div className="text-5xl font-bold text-yellow-600 mb-3">65%</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Patient Impact</h3>
              <p className="text-gray-600">Of patients experience treatment delays due to inefficient UM processes</p>
            </div>
          </div>
        </div>
      </section>

      <section id="solutions" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive AI-powered platforms that address your most critical healthcare operations challenges
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="group relative bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-10 hover:shadow-2xl transition-all duration-300 border-2 border-teal-100">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="inline-block bg-teal-600 text-white px-3 py-1 rounded-full text-xs font-semibold mb-3">
                    UTILIZATION MANAGEMENT
                  </div>
                  <h3 className="text-4xl font-bold text-gray-900 mb-2">CareAxis</h3>
                  <p className="text-lg text-teal-700 font-semibold">Smart UM Platform for Health Payers</p>
                </div>
                <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg">
                  <BarChart3 className="w-7 h-7 text-white" />
                </div>
              </div>
              
              <p className="text-gray-700 mb-8 leading-relaxed text-lg">
                AI-enabled medical management and utilization management solution designed for health plan payers. Reduce authorization processing from $3.50 to under $1 per auth with intelligent automation.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">AI-Powered Fax Intake</div>
                    <div className="text-sm text-gray-600">Automated processing of all formats including handwritten documents</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Automated Approvals</div>
                    <div className="text-sm text-gray-600">Configurable rules engine for instant authorizations</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Smart Queue Management</div>
                    <div className="text-sm text-gray-600">Automated task assignment and workload balancing</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Seamless Integration</div>
                    <div className="text-sm text-gray-600">Real-time APIs with claims, providers, and enterprise systems</div>
                  </div>
                </div>
              </div>

              <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="w-full py-4 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2 shadow-lg">
                <span>Learn More</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            <div className="group relative bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-10 hover:shadow-2xl transition-all duration-300 border-2 border-cyan-100">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="inline-block bg-cyan-600 text-white px-3 py-1 rounded-full text-xs font-semibold mb-3">
                    CORRESPONDENCE MANAGEMENT
                  </div>
                  <h3 className="text-4xl font-bold text-gray-900 mb-2">LetterPro+</h3>
                  <p className="text-lg text-cyan-700 font-semibold">Enterprise Document Intelligence</p>
                </div>
                <div className="w-14 h-14 bg-cyan-600 rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg">
                  <FileText className="w-7 h-7 text-white" />
                </div>
              </div>
              
              <p className="text-gray-700 mb-8 leading-relaxed text-lg">
                Centralized document and mail management platform with AI-powered insights. Store, track, and manage all correspondence in one secure location with seamless fulfillment vendor integration.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-cyan-600 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Unified Repository</div>
                    <div className="text-sm text-gray-600">All mail and documents in one centralized, searchable location</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-cyan-600 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Fulfillment Integration</div>
                    <div className="text-sm text-gray-600">Direct integration with fulfillment vendors for seamless operations</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-cyan-600 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Response Tracking</div>
                    <div className="text-sm text-gray-600">AI-powered tracking of mail responses and engagement analytics</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-cyan-600 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Advanced Analytics</div>
                    <div className="text-sm text-gray-600">Comprehensive reporting and data-driven insights</div>
                  </div>
                </div>
              </div>

              <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="w-full py-4 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2 shadow-lg">
                <span>Learn More</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-teal-100 to-cyan-100 rounded-2xl p-8 border-2 border-teal-200">
            <div className="flex items-center justify-between flex-wrap gap-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900">Seamless Integration</h4>
                  <p className="text-gray-700">CareAxis integrates directly with LetterPro+ for automated letter generation and tracking</p>
                </div>
              </div>
              <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="px-6 py-3 bg-white text-teal-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold shadow-md">
                View Integration
              </button>
            </div>
          </div>
        </div>
      </section>

      <section id="why-us" className="py-20 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Healthcare Leaders Choose Maverant
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Enterprise-grade solutions built for scale, security, and seamless integration
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-800 rounded-xl p-8 hover:bg-gray-750 transition-all duration-300 border border-gray-700 hover:border-teal-500">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center mb-6">
                <Shield className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Security First</h3>
              <p className="text-gray-400 mb-4">
                Multi-layered protection with industry-certified security standards. Deploy within your dedicated VPC or on-premises, air-gapped behind your firewall.
              </p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                  <span>HIPAA Adherence</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                  <span>End-to-end data encryption</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                  <span>Role-based access controls</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-800 rounded-xl p-8 hover:bg-gray-750 transition-all duration-300 border border-gray-700 hover:border-cyan-500">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center mb-6">
                <Zap className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Flexible Deployment</h3>
              <p className="text-gray-400 mb-4">
                Deploy in your cloud environment or on-premises. Full control over your data with Azure, AWS, or Google Cloud support.
              </p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                  <span>Cloud or on-premises options</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                  <span>Dedicated VPC environment</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                  <span>Multi-cloud architecture</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-800 rounded-xl p-8 hover:bg-gray-750 transition-all duration-300 border border-gray-700 hover:border-purple-500">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mb-6">
                <Brain className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Deep Customization</h3>
              <p className="text-gray-400 mb-4">
                Configure workflows, rules, and policies to match your exact processes. No coding required with our visual configuration tools.
              </p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <span>Visual workflow builder</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <span>Configurable rules engine</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <span>Custom clinical guidelines</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-800 rounded-xl p-8 hover:bg-gray-750 transition-all duration-300 border border-gray-700 hover:border-teal-500">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center mb-6">
                <Users className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Trusted Partnership</h3>
              <p className="text-gray-400 mb-4">
                Decade-long relationships with major healthcare organizations. We understand your workflows, systems, and challenges.
              </p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                  <span>Established since 2011</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                  <span>Healthcare-focused expertise</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                  <span>Long-term client relationships</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-800 rounded-xl p-8 hover:bg-gray-750 transition-all duration-300 border border-gray-700 hover:border-cyan-500">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center mb-6">
                <Clock className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Rapid Implementation</h3>
              <p className="text-gray-400 mb-4">
                Go live in 6 months with our proven methodology. Deploy customized workflows in days, not months, with continuous support.
              </p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                  <span>6-month implementation timeline</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                  <span>Onshore and offshore teams</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                  <span>Dedicated project management</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-800 rounded-xl p-8 hover:bg-gray-750 transition-all duration-300 border border-gray-700 hover:border-purple-500">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Measurable ROI</h3>
              <p className="text-gray-400 mb-4">
                Achieve quantifiable results with cost reduction, improved efficiency, and enhanced provider and member satisfaction.
              </p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <span>70% reduction in auth costs</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <span>ROI within 12 months</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <span>Improved provider satisfaction</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gray-800 rounded-2xl p-10 border border-gray-700 mb-16">
            <h3 className="text-2xl font-bold mb-8 text-center">Our Heritage and Expertise</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-xl font-semibold text-teal-400 mb-4">Backed by Hvantage Technologies</h4>
                <p className="text-gray-400 mb-4">
                  Established in 2011 in Los Angeles with offices in Indore, India. Hvantage is a leading digital solutions and technology consulting company serving Healthcare and Finance sectors.
                </p>
                <p className="text-gray-400">
                  Backed by DCNPL Ltd, a $150M group with diverse investments in Real Estate, Technology, and Telecom.
                </p>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-cyan-400 mb-4">Core Capabilities</h4>
                <ul className="space-y-3 text-gray-400">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                    <span>Innovation-driven design and exceptional user experience</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                    <span>Advanced data management, analytics, AI, and mobile solutions</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                    <span>Enterprise cloud implementation expertise (Azure, AWS, Google Cloud)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                    <span>Offshore and onsite teams with proven project management and DevOps</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-2xl p-10 border border-gray-700">
            <h3 className="text-2xl font-bold mb-6 text-center">Enterprise-Grade Technology Stack</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl mx-auto mb-3 flex items-center justify-center">
                  <Brain className="w-8 h-8" />
                </div>
                <div className="font-semibold">Azure AI</div>
                <div className="text-sm text-gray-400">Cognitive Services</div>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl mx-auto mb-3 flex items-center justify-center">
                  <Shield className="w-8 h-8" />
                </div>
                <div className="font-semibold">Cloud Secure</div>
                <div className="text-sm text-gray-400">Azure and AWS</div>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl mx-auto mb-3 flex items-center justify-center">
                  <Zap className="w-8 h-8" />
                </div>
                <div className="font-semibold">Real-time APIs</div>
                <div className="text-sm text-gray-400">High Performance</div>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl mx-auto mb-3 flex items-center justify-center">
                  <Lock className="w-8 h-8" />
                </div>
                <div className="font-semibold">Compliance</div>
                <div className="text-sm text-gray-400">HIPAA Adherence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="py-20 px-6 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Get in Touch
            </h2>
            <p className="text-xl text-gray-600">
              Ready to transform your healthcare operations? Contact us today for a personalized demo.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-2xl p-10 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send us a message</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="john@company.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="Your Healthcare Organization"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Tell us about your needs..."
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg hover:from-teal-600 hover:to-cyan-700 transition-all font-semibold text-lg shadow-lg flex items-center justify-center space-x-2"
                >
                  <span>Send Message</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            </div>

            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Email</div>
                      <a href="mailto:info@maverant.com" className="text-gray-600 hover:text-teal-600 transition-colors">info@maverant.com</a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-2">Phone</div>
                      <div className="space-y-1">
                        <a href="tel:+18186616301" className="block text-gray-600 hover:text-teal-600 transition-colors">+1 (818) 661-6301</a>                      </div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Address</div>
                      <p className="text-gray-600 leading-relaxed">22048 Sherman Way<br />Suite 306<br />Los Angeles, CA 91303</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-teal-600 to-cyan-600 rounded-2xl p-8 text-white shadow-xl">
                <h4 className="text-xl font-bold mb-3">Ready to see CareAxis in action?</h4>
                <p className="text-teal-50 mb-6">
                  Schedule a personalized demo and discover how we can transform your healthcare operations.
                </p>
                <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="w-full px-6 py-3 bg-white text-teal-700 rounded-lg hover:bg-gray-50 transition-all font-semibold shadow-md cursor-pointer">
                  Book a Demo
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-gray-900 text-gray-400 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">M</span>
                </div>
                <span className="text-xl font-bold text-white">Maverant</span>
              </div>
              <p className="text-sm leading-relaxed">
                Transforming healthcare operations with AI-powered enterprise solutions for payers and providers.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Solutions</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-teal-400 transition-colors">CareAxis UM Platform</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors">LetterPro+ Correspondence</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors">Integration Services</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors">API Documentation</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-teal-400 transition-colors">About Maverant</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors">Case Studies</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors">Contact Us</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-teal-400 transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors">Security and Compliance</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">&copy; 2025 Maverant Inc. All rights reserved.</p>
            <div className="flex items-center space-x-6 text-sm">
              <span className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-teal-500" />
                <span>HIPAA Compliant</span>
              </span>
             </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
/* force rebuild */
